import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dices } from 'lucide-react';
import SearchStatus from '../components/SearchStatus';
import { ALL_TASKS, getTaskUrl } from '../constants/tasks';
import { SECTION_NAV_ITEMS } from '../config/navItems';

const sectionAccentClass: Record<string, string> = {
  grammar: 'grammar',
  vocabulary: 'vocabulary',
  reading: 'reading',
  writing: 'writing',
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set());

  useEffect(() => {
    const loadCompletedTasks = () => {
      const savedCompletedTasks = localStorage.getItem('anglopi-completed-tasks');
      if (savedCompletedTasks) {
        try {
          const parsed = JSON.parse(savedCompletedTasks);
          setCompletedTasks(new Set(parsed));
        } catch (error) {
          console.error('Error loading completed tasks:', error);
        }
      }
    };

    loadCompletedTasks();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'anglopi-completed-tasks') {
        loadCompletedTasks();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const handleFocus = () => {
      loadCompletedTasks();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const totalTasks = ALL_TASKS.length;
  const completedCount = completedTasks.size;
  const remainingCount = totalTasks - completedCount;
  const progressPercent = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
  const currentYear = new Date().getFullYear();

  const sectionTiles = useMemo(
    () =>
      SECTION_NAV_ITEMS.map((item) => {
        const sectionKey = item.path.replace('/', '');
        return {
          ...item,
          accent: sectionAccentClass[sectionKey] ?? 'grammar',
        };
      }),
    []
  );

  const handleRandomTask = () => {
    const availableTasks = ALL_TASKS.filter((task) => !completedTasks.has(task.id));

    if (availableTasks.length === 0) {
      alert("Congratulations! You've completed all available tasks! 🎉");
      return;
    }

    const randomIndex = Math.floor(Math.random() * availableTasks.length);
    const randomTask = availableTasks[randomIndex];
    navigate(getTaskUrl(randomTask));
  };

  return (
    <div className="home">
      <div className="home-backdrop" aria-hidden="true">
        <span className="home-blob home-blob--1" />
        <span className="home-blob home-blob--2" />
        <span className="home-blob home-blob--3" />
      </div>

      <SearchStatus />

      <div className="welcome-content">
        <div className="home-hero">
          <div className="home-intro">
            <p className="home-subtitle">
              Pick a topic, or tap Surprise Me below.
            </p>

            <div className="home-section-grid" aria-label="Choose a section">
              {sectionTiles.map(({ path, label, icon: Icon, accent }) => (
                <Link
                  key={path}
                  to={path}
                  className={`home-section-card home-section-card--${accent}`}
                >
                  <span className="home-section-card-icon" aria-hidden="true">
                    <Icon strokeWidth={2} />
                  </span>
                  <span className="home-section-card-label">{label}</span>
                </Link>
              ))}
            </div>

            <div className="random-task-section">
              <button
                type="button"
                className="random-task-button"
                onClick={handleRandomTask}
                title="Start with a random challenge"
              >
                <Dices className="random-task-button-icon" aria-hidden="true" />
                Surprise Me
              </button>
            </div>
          </div>

          <div className="home-footer">
            {totalTasks > 0 && (
              <div
                className="home-progress"
                role="progressbar"
                aria-valuenow={progressPercent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${progressPercent}% of tasks completed`}
              >
                <div className="home-progress-track">
                  <div
                    className="home-progress-fill"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}

            <p className="home-copyright">© {currentYear} Skul.no - Anglopi</p>

            <div className="home-stats">
              <div className="stat-item">
                <span className="stat-number">{totalTasks}</span>
                <span className="stat-label">Total Tasks</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{completedCount}</span>
                <span className="stat-label">Completed</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{remainingCount}</span>
                <span className="stat-label">Remaining</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
